import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '../link';
import { GameTitle } from './cinema';
import { soundedPointerHandlers } from '../sound';
import styles from './store.module.css';
import { Icon, ICON_JOB } from './icon';

export { styles as store };

/**
 * Store furniture: capsules, rails, badges, hero.
 *
 * A storefront is a grid of art with prices on it. Everything here exists to
 * make that shape possible, and every piece degrades honestly when the data
 * behind it does not exist yet: a title with no capsule gets a plate saying so
 * rather than a stock image, and a rail with nothing in it does not render at
 * all rather than showing placeholders.
 */

export interface CapsuleTitle {
  slug: string;
  name: string;
  studio: string;
  /**
   * Where the capsule goes. Defaults to this site's own store page.
   *
   * A path, and one deployment serves every page a capsule can point at, so
   * `/g/<slug>` is all this needs. It used to say the creators surface had to
   * pass an absolute `siteUrl('store', …)` — true while that surface was a
   * separate deployment, and wrong ever since. An origin built here is one
   * nothing downstream corrects, which in a dev session meant a capsule linking
   * to a port nothing listens on.
   */
  href?: string;
  /**
   * The landscape capsule. Null until a title has one (#132).
   *
   * Steam's main capsule, 616×353 — see `lib/title-art/sizes.ts` for why every
   * size here is Steam's to the pixel. Null renders the art-pending plate,
   * which is the behaviour this component has always had and is what lets art
   * arrive one title at a time.
   */
  artUrl?: string | null;
  /**
   * The portrait one, for a grid or rail drawn `portrait`.
   *
   * Steam's library capsule, 600×900. A separate field rather than the caller
   * choosing, because one page draws both orientations from the same shelf —
   * `index.astro` has landscape rails under a hero — so a view model carrying
   * whichever the caller happened to want would be a view model that cannot be
   * reused between them.
   *
   * Falls back to `artUrl` when absent, which is the honest degradation: a
   * landscape image letterboxed in a portrait slot reads as a design choice,
   * and an art-pending plate beside seven real capsules reads as a fault.
   */
  portraitArtUrl?: string | null;
  /**
   * The full-bleed image behind `StoreHero`. Steam's library hero, 3840×1240.
   *
   * Its own field because the hero is the one surface where the capsule is the
   * wrong picture: a 616×353 capsule stretched across 1920 pixels of page is
   * the single most obvious way for a store to look cheap, and the capsule is
   * what `artUrl` holds.
   */
  heroArtUrl?: string | null;
  pageBackgroundUrl?: string | null;
  logoUrl?: string | null;
  /**
   * Where the title is, when that is not "on sale" (#138).
   *
   * Set to `Coming soon` for a title the store lists but nobody can buy yet.
   * When it is set the capsule shows it **instead of** the price, which is the
   * whole point rather than a side effect: an unreleased title has no price the
   * public is entitled to (#139), and the alternative in that slot is the
   * `Not priced yet` fallback — a sentence about our own data entry, shown to a
   * shopper who only wanted to know when they can play.
   *
   * A string rather than a boolean because the state is the caller's to name.
   * This component knows where the label goes; `toCapsule` knows that
   * `coming_soon` is the state that has one.
   */
  releaseLabel?: string | null;
  /*
    `badge` was here, and it is gone.

    It carried one standing fact about a title: whether GOODBUILD had made the
    game as well as published it. It was drawn in the quiet treatment — an
    outline in the rule colour rather than the accent — because it was
    disclosure rather than promotion, and a house title shouting louder than a
    partner's on the same grid would have been the thumb on the scale section 2
    forbids.

    GOODBUILD signs nobody, so every title on this grid is ours and the fact
    distinguishes nothing. The field was a string rather than a boolean so a
    second standing fact would not need a second flag; if one ever arrives, this
    is the shape to bring back.
  */
}

/**
 * One game.
 *
 * The art area is the point. A store card that is three lines of text is a
 * database row with a border, which is what makes a games site read as a
 * business site.
 *
 * ## The card is an object in space
 *
 * It takes the pointer and turns towards it, its type lifts off the face, and
 * an accent frame behind it comes out from whichever edge is going away. This
 * came off the waitlist's shelf, where it was the loudest thing on the page and
 * was allowed to be for a reason that still holds: **nothing on a capsule is a
 * measurement.** A card carrying a name, a studio and a price can catch the
 * light. The rule was never "the store does not move", it was "a figure never
 * displays a value it did not have", and nothing here writes a number.
 *
 * ### How it is built
 *
 * Three elements rather than two, and the nesting is the whole trick:
 *
 *   `.capsule` is the link, and it supplies the **perspective**. An element
 *   cannot supply its own — one that tries flattens to a scale — so the lens
 *   has to be on the parent of the thing that turns.
 *
 *   `.capsuleFace` is what turns, and carries the card's ground and border. It
 *   is `preserve-3d`, which is what makes the layers inside it real rather than
 *   painted, and also why nothing in it may take `overflow: hidden` — that
 *   silently flattens the whole subtree back into the page. The one element
 *   that clips is `.capsuleArt`, whose only child is an image and which wants
 *   to be flat anyway.
 *
 *   `.capsuleHalo` is the accent frame, larger than the card and further away,
 *   so at rest it hides exactly behind it.
 *
 * ### The turn is this component's, and it used to be a page's
 *
 * `data-tilt` was an attribute, read by forty lines of inline script in the
 * storefront's `Base.astro` — the only JavaScript that site shipped, written
 * that way because a page which ships no JavaScript cannot import a component
 * that does. That layout is gone and the attribute would have been an attribute
 * nothing reads: a shelf of cards that no longer turn, with nothing failing.
 *
 * So it is `useTilt` below, on the element itself. Two handlers rather than a
 * `querySelectorAll` over the document, which is the part that gets better in
 * the move: a card that arrives from a later render is wired because it is the
 * same component, rather than because somebody remembered to re-run a scan.
 *
 * Everything else is still `:hover` and `:focus-visible` in the stylesheet: the
 * lift, the halo, the type coming forward, the art pushing in. A browser that
 * never runs this gets a card that still has depth and still answers the
 * keyboard; the hook only decides which way it is facing.
 */

/** Degrees at the edge. Each surface scales it with `--tilt-gain`. */
const TILT = 6;

/**
 * A card that turns towards the pointer.
 *
 * **Fine pointers only, and never under reduced motion**, both checked on the
 * first move rather than once at module scope: a laptop with a trackpad and a
 * touchscreen can be either at different moments, and the operating-system
 * motion preference changes without a reload.
 *
 * The rect is read inside an animation frame rather than in the event, and that
 * is the whole reason there is a frame in the middle of it:
 * `getBoundingClientRect` forces the browser to lay the page out, and a pointer
 * crossing a card fires often enough that doing it per event is a reflow a
 * hundred times a second.
 *
 * **A card turning is not a measurement moving.** Nothing here writes a number,
 * a width or an opacity — only two angles on a frame. Section 06 and hard rule
 * 14 are about figures displaying values they never had, which is why the shelf
 * may move while a stat may not.
 */
function useTilt(): {
  ref: React.RefObject<HTMLElement | null>;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerLeave: () => void;
} {
  const ref = useRef<HTMLElement | null>(null);
  const frame = useRef(0);
  const pointer = useRef({ x: 0, y: 0 });

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      return;
    }

    pointer.current = { x: event.clientX, y: event.clientY };

    const card = ref.current;
    if (!card) return;
    card.setAttribute('data-tilting', '');
    if (frame.current) return;

    frame.current = requestAnimationFrame(() => {
      frame.current = 0;

      const box = card.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;

      /*
       * Where the pointer is on the card, as −0.5 at one edge to 0.5 at the
       * other, so the middle is nought and neither axis needs a sign fixing up
       * afterwards.
       */
      const across = (pointer.current.x - box.left) / box.width - 0.5;
      const down = (pointer.current.y - box.top) / box.height - 0.5;

      /*
       * The vertical axis is inverted on purpose: a pointer near the top should
       * tip the top of the card *away*, which is a negative rotation about x.
       * Getting this backwards is the version that feels wrong without anybody
       * being able to say why.
       */
      card.style.setProperty('--tilt-y', `${(across * TILT * 2).toFixed(2)}deg`);
      card.style.setProperty('--tilt-x', `${(-down * TILT * 2).toFixed(2)}deg`);
    });
  }, []);

  /*
   * Back to flat when the pointer leaves, and the attribute goes with it — which
   * is what puts the slow curve back, so the card settles rather than snapping.
   * Removing the properties rather than writing `0deg` hands the resting angle
   * back to the stylesheet, where the default is.
   */
  const onPointerLeave = useCallback(() => {
    if (frame.current) {
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    }

    const card = ref.current;
    if (!card) return;
    card.removeAttribute('data-tilting');
    card.style.removeProperty('--tilt-x');
    card.style.removeProperty('--tilt-y');
  }, []);

  return { ref, onPointerMove, onPointerLeave };
}
export function Capsule({
  title,
  portrait = false,
}: {
  title: CapsuleTitle;
  portrait?: boolean;
}): React.ReactElement {
  const href = title.href ?? `/g/${title.slug}`;
  const className = `${styles.capsule} ${portrait ? styles.capsulePortrait : ''}`.trim();
  const tilt = useTilt();

  /*
    Which of the two shapes this card is wearing (#132).

    The portrait one when the card is portrait and there is one, and the
    landscape one otherwise — including for a portrait card whose title has no
    library capsule. Letterboxing a landscape image is a visible compromise and
    an art-pending plate beside seven real capsules is a visible fault, and of
    the two the compromise is the one a visitor reads as deliberate.
  */
  const art = (portrait ? (title.portraitArtUrl ?? title.artUrl) : title.artUrl) ?? null;

  // An absolute href leaves this origin for another of the three sites, and
  // `next/link` cannot navigate across one. Same rule as `Cta`.
  const Wrapper = href.startsWith('http://') || href.startsWith('https://') ? 'a' : Link;

  return (
    /*
      `data-tilt` stays on the element even though nothing scans for it any more.

      It is what `store-and-spaces.test.ts` reads to prove the turn is on the
      capsule and on nothing that carries a figure, and it is the attribute a
      stylesheet or a future scan would key off. The handlers beside it are what
      actually make the card move — see `useTilt`.
    */
    <Wrapper
      href={href}
      className={className}
      data-tilt=""
      ref={tilt.ref as never}
      onPointerMove={tilt.onPointerMove}
      onPointerLeave={tilt.onPointerLeave}
      {...soundedPointerHandlers}
    >
      <div className={styles.capsuleFace}>
        {/*
          Behind the card and larger than it, so it is invisible until the card
          turns. See the note in the stylesheet for the arithmetic — it has to
          survive being scaled down by the perspective.
        */}
        <span className={styles.capsuleHalo} aria-hidden="true" />

        <div className={styles.capsuleArt}>
          {art ? (
            // Plain img: capsule art is developer-supplied and lives on object
            // storage, so there is no build-time size to give next/image.
            // eslint-disable-next-line @next/next/no-img-element
            <img src={art} alt="" loading="lazy" />
          ) : (
            <span className={styles.capsulePlate}>{title.name}</span>
          )}
        </div>

        <div className={styles.capsuleBody}>
          <h3 className={styles.capsuleTitle}>{title.name}</h3>
          <span className={styles.capsuleStudio}>{title.studio}</span>

          <div className={styles.capsuleFoot}>
            <span className={styles.capsuleAmount}>
              {title.releaseLabel ? (
                <span className={styles.capsuleStatus}>{title.releaseLabel}</span>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

/**
 * Volt, and only for something live at this moment.
 *
 * The count carries its own unit rather than sitting next to an unlabelled
 * number, and the component takes a count rather than a boolean so it cannot
 * render "LIVE" with nothing behind it.
 *
 * `ICON_JOB.live` is the signal-bars picture, which section 06 reserves for
 * exactly this and nothing else. It replaces the plain dot that stood here: a
 * dot is a shape, and the set has a drawing that means this.
 */
export function LiveBadge({ viewers }: { viewers: number }): React.ReactElement {
  return (
    <span className={`${styles.badge} ${styles.badgeLive}`}>
      <Icon name={ICON_JOB.live} size={16} />
      {viewers.toLocaleString('en-GB')} watching
    </span>
  );
}

export function CapsuleGrid({
  titles,
  portrait = false,
}: {
  titles: readonly CapsuleTitle[];
  portrait?: boolean;
}): React.ReactElement {
  return (
    <div className={`${styles.grid} ${portrait ? styles.gridPortrait : ''}`.trim()}>
      {titles.map((title) => (
        <Capsule key={title.slug} title={title} portrait={portrait} />
      ))}
    </div>
  );
}

/**
 * A titled row of games.
 *
 * Returns null when empty rather than rendering a heading over nothing. A rail
 * called "Live now" with no games under it tells a visitor the store is
 * broken; not drawing it tells them nothing, which is correct, because there
 * is nothing to say.
 */
export function Rail({
  heading,
  titles,
  moreHref,
  moreLabel = 'See all',
}: {
  heading: string;
  titles: readonly CapsuleTitle[];
  moreHref?: string;
  moreLabel?: string;
}): React.ReactElement | null {
  if (titles.length === 0) return null;

  return (
    <section>
      <div className={styles.railHead}>
        <h2 className="gb-subhead">{heading}</h2>
        {moreHref ? (
          <Link href={moreHref} className={styles.railLink}>
            {moreLabel}
            <Icon name="arrow-right" size={16} />
          </Link>
        ) : null}
      </div>
      <div className={styles.rail}>
        {titles.map((title) => (
          <Capsule key={title.slug} title={title} />
        ))}
      </div>
    </section>
  );
}

/**
 * The featured title, full width.
 *
 * Renders nothing without a title. An empty hero filled with stock art is
 * exactly the fabrication section 29.3 forbids, and on a store whose whole
 * argument is honesty it would be the worst possible place to start.
 */
export function StoreHero({
  title,
  tall = false,
  headingLevel = 'h2',
  children,
}: {
  title: CapsuleTitle | null;
  tall?: boolean;
  headingLevel?: 'h1' | 'h2';
  children?: React.ReactNode;
}): React.ReactElement | null {
  if (!title) return null;

  const heroClassName = tall ? `${styles.hero ?? ''} ${styles.heroTall ?? ''}`.trim() : styles.hero;

  return (
    <section className={heroClassName}>
      {title.heroArtUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img className={styles.heroArt} src={title.heroArtUrl} alt="" />
      ) : null}
      <div className={styles.heroInner}>
        <div className={styles.heroMeta}>
          <span className={styles.capsuleStudio} style={{ color: 'rgba(255,255,255,0.7)' }}>
            {title.studio}
          </span>
          {title.releaseLabel ? (
            <span className={`${styles.badge} ${styles.badgeCountdown}`}>
              <Icon name={ICON_JOB.countdown} size={16} />
              {title.releaseLabel}
            </span>
          ) : null}
        </div>

        <GameTitle title={title.name} logoUrl={title.logoUrl ?? null} as={headingLevel} />

        {children}

        <div className={styles.heroActions}>
          <Link href={`/g/${title.slug}`} className={styles.heroBuy} {...soundedPointerHandlers}>
            Have a look
          </Link>
        </div>
      </div>
    </section>
  );
}

export interface GalleryItem {
  id: string;
  kind: 'trailer' | 'screenshot';
  url: string;
  posterUrl: string | null;
}

export function MediaGallery({
  items,
  gameName,
}: {
  items: readonly GalleryItem[];
  gameName: string;
}): React.ReactElement | null {
  const [shownIndex, setShownIndex] = useState(0);
  const [fullScreenAt, setFullScreenAt] = useState<number | null>(null);

  useEffect(() => {
    setShownIndex(0);
    setFullScreenAt(null);
  }, [gameName]);

  if (items.length === 0) return null;

  const shown = items[Math.min(shownIndex, items.length - 1)];
  if (!shown) return null;

  const trailerCount = items.filter((item) => item.kind === 'trailer').length;
  const screenshotCount = items.length - trailerCount;
  const pictures = items.filter((item) => item.kind === 'screenshot');

  function describe(item: GalleryItem, index: number): string {
    const placeAmongKind = items.filter(
      (other, order) => other.kind === item.kind && order <= index,
    ).length;
    return item.kind === 'trailer'
      ? `${gameName} trailer ${placeAmongKind}`
      : `${gameName} screenshot ${placeAmongKind}`;
  }

  function moveBy(step: number): void {
    setShownIndex((current) => {
      const next = current + step;
      if (next < 0) return items.length - 1;
      if (next >= items.length) return 0;
      return next;
    });
  }

  return (
    <section className={styles.gallery ?? ''} aria-label={`Pictures and videos of ${gameName}`}>
      <div className={styles.galleryStage ?? ''}>
        {shown.kind === 'trailer' ? (
          <video
            key={shown.id}
            src={shown.url}
            controls
            preload="metadata"
            playsInline
            {...(shown.posterUrl === null ? {} : { poster: shown.posterUrl })}
            aria-label={describe(shown, shownIndex)}
          />
        ) : (
          <button
            type="button"
            className={styles.galleryOpen ?? ''}
            onClick={() => setFullScreenAt(pictures.findIndex((item) => item.id === shown.id))}
            aria-label={`See ${describe(shown, shownIndex)} full screen`}
            {...soundedPointerHandlers}
          >
            <img key={shown.id} src={shown.url} alt={describe(shown, shownIndex)} />
            <span className={styles.galleryOpenHint ?? ''} aria-hidden="true">
              <Icon name="magnifying-glass" size={16} />
              Full screen
            </span>
          </button>
        )}
      </div>

      {items.length > 1 ? (
        <div
          className={styles.galleryStrip ?? ''}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              moveBy(1);
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              moveBy(-1);
            }
          }}
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`${styles.galleryThumb ?? ''} ${
                index === shownIndex ? (styles.galleryThumbCurrent ?? '') : ''
              }`}
              aria-current={index === shownIndex ? 'true' : undefined}
              onClick={() => setShownIndex(index)}
              {...soundedPointerHandlers}
            >
              {item.kind === 'screenshot' ? (
                <img src={item.url} alt={describe(item, index)} loading="lazy" />
              ) : item.posterUrl !== null ? (
                <img src={item.posterUrl} alt={describe(item, index)} loading="lazy" />
              ) : (
                <span className={styles.galleryThumbPlate ?? ''}>{describe(item, index)}</span>
              )}
              {item.kind === 'trailer' ? (
                <span className={styles.galleryPlayBadge ?? ''} aria-hidden="true">
                  Play
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      <Lightbox
        shots={pictures.map((item, order) => ({
          url: item.url,
          alt: `${gameName} screenshot ${order + 1}`,
        }))}
        at={fullScreenAt}
        onMove={setFullScreenAt}
        onClose={() => setFullScreenAt(null)}
      />

      <p className={styles.galleryCount ?? ''}>
        {trailerCount > 0 ? `${trailerCount} ${trailerCount === 1 ? 'video' : 'videos'}` : null}
        {trailerCount > 0 && screenshotCount > 0 ? ' · ' : null}
        {screenshotCount > 0
          ? `${screenshotCount} ${screenshotCount === 1 ? 'picture' : 'pictures'}`
          : null}
      </p>
    </section>
  );
}

export interface LightboxShot {
  url: string;
  alt: string;
}

export function Lightbox({
  shots,
  at,
  onMove,
  onClose,
}: {
  shots: readonly LightboxShot[];
  at: number | null;
  onMove: (next: number) => void;
  onClose: () => void;
}): React.ReactElement {
  const frame = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const pane = frame.current;
    if (pane === null) return;
    if (at !== null && !pane.open) pane.showModal();
    if (at === null && pane.open) pane.close();
  }, [at]);

  const step = useCallback(
    (by: number) => {
      if (at === null || shots.length === 0) return;
      onMove((at + by + shots.length) % shots.length);
    },
    [at, shots.length, onMove],
  );

  const shown = at === null ? null : (shots[at] ?? null);
  const position = at === null ? 0 : at + 1;

  return (
    <dialog
      ref={frame}
      className={styles.lightbox ?? ''}
      aria-label={shown ? shown.alt : 'Picture'}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === frame.current) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          step(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          step(-1);
        }
      }}
    >
      {shown ? (
        <div className={styles.lightboxInner ?? ''}>
          <img className={styles.lightboxShot ?? ''} src={shown.url} alt={shown.alt} />

          <div className={styles.lightboxBar ?? ''}>
            {shots.length > 1 ? (
              <button
                type="button"
                className={styles.lightboxStep ?? ''}
                onClick={() => step(-1)}
                aria-label="The one before"
                {...soundedPointerHandlers}
              >
                <Icon name="angle-left" size={16} />
              </button>
            ) : null}

            <span className={styles.lightboxCount ?? ''}>
              {position} of {shots.length}
            </span>

            {shots.length > 1 ? (
              <button
                type="button"
                className={styles.lightboxStep ?? ''}
                onClick={() => step(1)}
                aria-label="The next one"
                {...soundedPointerHandlers}
              >
                <Icon name="angle-right" size={16} />
              </button>
            ) : null}

            <button
              type="button"
              className={styles.lightboxShut ?? ''}
              onClick={onClose}
              {...soundedPointerHandlers}
            >
              <Icon name="xmark" size={16} />
              Close
            </button>
          </div>
        </div>
      ) : null}
    </dialog>
  );
}
